import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad, useTriggerLoad } from "@redotech/react-util/load";
import CloseIcon from "@redotech/redo-icons/x-close.svg";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import type { RedoFulfillmentLocation } from "@redotech/redo-model/fulfillments/fulfillment-location";
import type { ProductId } from "@redotech/redo-model/ids";
import type { SuppliesInventoryTrackedProductType } from "@redotech/redo-model/supplies";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoIncrementDecrement } from "@redotech/redo-web/arbiter-components/increment-decrement/redo-increment-decrement";
import { RedoBaseModal } from "@redotech/redo-web/arbiter-components/modal/redo-base-modal";
import { RedoModalFooter } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { RedoRadioGroup } from "@redotech/redo-web/arbiter-components/radio/redo-radio-group";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useCallback, useState } from "react";
import * as styles from "./supplies-onboarding-modal.module.css";
import {
  type SuppliesOnboardingStep,
  SuppliesOnboardingStepper,
  suppliesOnboardingSteps,
} from "./supplies-onboarding-stepper";

export interface EligibleItem {
  productId: ProductId;
  title: string;
  type: SuppliesInventoryTrackedProductType;
  quantityPerCase: number;
}

export interface InventorySetupData {
  productId: ProductId;
  onHandQuantity: number | undefined;
  reorderThreshold: number | undefined;
  casesToReorder: number | undefined;
}

function formatAddress(location: RedoFulfillmentLocation): string {
  const parts = [
    location.address.address1,
    location.address.city,
    location.address.provinceCode ?? location.address.province,
    location.address.zip,
  ].filter(Boolean);
  return parts.join(", ");
}

export const SuppliesOnboardingModal = memo(function SuppliesOnboardingModal({
  eligibleItems,
  onClose,
  onOneTimeOrder,
  onSelectLocation,
}: {
  eligibleItems: EligibleItem[];
  onClose(): void;
  onOneTimeOrder(): Promise<void>;
  onSelectLocation(
    locationId: string,
    inventoryData: InventorySetupData[],
  ): Promise<void>;
}) {
  const [currentStep, setCurrentStep] =
    useState<SuppliesOnboardingStep>("setup");
  const rpcClient = useRequiredContext(RedoMerchantRpcClientContext);

  const locationsLoad = useLoad(async () => {
    const { locations } = await rpcClient.getLocations(null);
    return locations.filter((l) => l.isActive);
  }, [rpcClient]);

  const [selectedId, setSelectedId] = useState<string | null>(null);

  const [inventoryData, setInventoryData] = useState<InventorySetupData[]>(() =>
    eligibleItems.map((item) => ({
      productId: item.productId,
      onHandQuantity: undefined,
      reorderThreshold: undefined,
      casesToReorder: undefined,
    })),
  );

  const updateInventoryItem = useCallback(
    (
      productId: ProductId,
      field: "onHandQuantity" | "reorderThreshold" | "casesToReorder",
      value: number | undefined,
    ) => {
      setInventoryData((prev) =>
        prev.map((item) =>
          item.productId === productId ? { ...item, [field]: value } : item,
        ),
      );
    },
    [],
  );

  const [submitState, triggerSubmit] = useTriggerLoad(async () => {
    if (selectedId) {
      await onSelectLocation(selectedId, inventoryData);
    }
  });

  const [oneTimeOrderState, triggerOneTimeOrder] = useTriggerLoad(async () => {
    await onOneTimeOrder();
  });

  const handleLocationSubmit = useCallback(() => {
    triggerSubmit();
  }, [triggerSubmit]);

  const currentStepIndex = suppliesOnboardingSteps.indexOf(currentStep);
  const isFirstStep = currentStepIndex === 0;
  const isLastStep = currentStepIndex === suppliesOnboardingSteps.length - 1;

  const handleBack = useCallback(() => {
    if (currentStepIndex > 0) {
      setCurrentStep(suppliesOnboardingSteps[currentStepIndex - 1]!);
    }
  }, [currentStepIndex]);

  const handleContinue = useCallback(() => {
    if (currentStepIndex < suppliesOnboardingSteps.length - 1) {
      setCurrentStep(suppliesOnboardingSteps[currentStepIndex + 1]!);
    }
  }, [currentStepIndex]);

  const locations = locationsLoad.value;

  const options =
    locations?.map((loc) => ({
      id: loc.externalId,
      label: loc.name,
      description: formatAddress(loc),
    })) ?? [];

  const continueDisabled = (() => {
    if (currentStep === "location" && !selectedId) {
      return "Please select a location";
    }
    return undefined;
  })();

  return (
    <RedoBaseModal
      isOpen
      onModalCloseRequested={onClose}
      otherClasses={[styles.modal]}
    >
      <Flex
        borderBottomWidth="1px"
        borderColor="primary"
        dir="column"
        gap="md"
        p="lg"
      >
        <Flex align="center" justify="space-between">
          <Text fontSize="sm" fontWeight="semibold">
            Inventory tracked supplies setup
          </Text>
          <RedoButton
            hierarchy="tertiary"
            IconLeading={CloseIcon}
            onClick={onClose}
            size="md"
          />
        </Flex>
        <Flex justify="center">
          <SuppliesOnboardingStepper
            currentStep={currentStep}
            steps={suppliesOnboardingSteps}
          />
        </Flex>
      </Flex>
      <div className={styles.content}>
        {currentStep === "setup" && (
          <Flex dir="column" gap="lg" p="3xl">
            <Text fontSize="sm" textColor="secondary">
              Good news! Your cart contains items that are eligible for
              inventory-based automatic reordering! Would you like to set that
              up?
            </Text>
          </Flex>
        )}
        {currentStep === "location" && (
          <Flex dir="column" gap="lg" p="3xl">
            <Text fontSize="sm" textColor="secondary">
              Choose which warehouse location this order should be shipped to.
              This warehouse will be where we start tracking inventory, so make
              sure it is a location that you actively fulfill from.
            </Text>
            {locationsLoad.pending ? (
              <Text fontSize="sm" textColor="tertiary">
                Loading locations...
              </Text>
            ) : options.length === 0 ? (
              <Text fontSize="sm" textColor="tertiary">
                No active locations found.
              </Text>
            ) : (
              <RedoRadioGroup
                options={options}
                setValue={(value) => setSelectedId(value)}
                value={selectedId ?? ""}
              />
            )}
          </Flex>
        )}
        {currentStep === "inventory" && (
          <Flex dir="column" gap="xl" p="3xl">
            <Text fontSize="sm" textColor="secondary">
              Set up initial inventory amounts and reorder thresholds for each
              eligible item.
            </Text>
            {eligibleItems.map((item) => {
              const data = inventoryData.find(
                (d) => d.productId === item.productId,
              );
              return (
                <Flex
                  borderBottomWidth="1px"
                  borderColor="primary"
                  borderStyle="solid"
                  dir="column"
                  gap="md"
                  key={item.productId}
                  pb="xl"
                >
                  <Text fontSize="sm" fontWeight="semibold">
                    {item.title}
                  </Text>
                  <Flex gap="xl">
                    <RedoIncrementDecrement
                      description="Individual units"
                      label="Current on-hand quantity"
                      min={0}
                      placeholder="0"
                      setValue={(value) =>
                        updateInventoryItem(
                          item.productId,
                          "onHandQuantity",
                          value,
                        )
                      }
                      size="small"
                      value={data?.onHandQuantity}
                    />
                    <RedoIncrementDecrement
                      description="Individual units"
                      label="Reorder threshold"
                      min={0}
                      placeholder="0"
                      setValue={(value) =>
                        updateInventoryItem(
                          item.productId,
                          "reorderThreshold",
                          value,
                        )
                      }
                      size="small"
                      value={data?.reorderThreshold}
                    />
                    <RedoIncrementDecrement
                      description="Cases"
                      label="Cases to reorder"
                      min={1}
                      placeholder="1"
                      setValue={(value) =>
                        updateInventoryItem(
                          item.productId,
                          "casesToReorder",
                          value,
                        )
                      }
                      size="small"
                      value={data?.casesToReorder}
                    />
                  </Flex>
                </Flex>
              );
            })}
          </Flex>
        )}
      </div>
      {isFirstStep ? (
        <RedoModalFooter
          primaryButton={{
            text: "Set up",
            onClick: handleContinue,
            size: "md",
          }}
          secondaryButton={{
            text: "One-time order only",
            onClick: () => triggerOneTimeOrder(),
            pending: oneTimeOrderState.pending,
            size: "md",
          }}
        />
      ) : isLastStep ? (
        <RedoModalFooter
          primaryButton={{
            text: "Continue to checkout",
            onClick: handleLocationSubmit,
            disabled: inventoryData.some(
              (d) =>
                d.onHandQuantity === undefined ||
                d.reorderThreshold === undefined ||
                d.casesToReorder === undefined,
            )
              ? "Please fill in all inventory fields"
              : undefined,
            pending: submitState.pending,
            size: "md",
          }}
          secondaryButton={{ text: "Back", onClick: handleBack, size: "md" }}
        />
      ) : (
        <RedoModalFooter
          primaryButton={{
            text: "Continue",
            onClick: handleContinue,
            disabled: continueDisabled,
            size: "md",
          }}
          secondaryButton={{ text: "Back", onClick: handleBack, size: "md" }}
        />
      )}
    </RedoBaseModal>
  );
});
